import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { ConfluenceIcon } from '@atlaskit/logo';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';

import appStoreBadge from './assets/appStoreBadge.svg';
import googlePlayBadge from './assets/googlePlayBadge.svg';
import qrCode from './assets/qrCode.svg';
import { useMobileAppPromoDismiss } from './useMobileAppPromoDismiss.ts';

const GOOGLE_PLAY_URL =
	'https://play.google.com/store/apps/details?id=com.atlassian.android.confluence.core&referrer=utm_source%3DconfluenceWeb%26utm_medium%3DUI%26utm_campaign%3DtestWebPopularFeedAudio';
const APP_STORE_URL =
	'https://apps.apple.com/app/apple-store/id1006971684?pt=696495&ct=testWebPopularFeedAudio&mt=8';

const i18n = defineMessages({
	eyebrow: {
		id: 'feed.mobile-app-promo.eyebrow',
		defaultMessage: 'Try the mobile app',
		description: 'Blue eyebrow label above the headline on the mobile app promotion card in feed',
	},
	headline: {
		id: 'feed.mobile-app-promo.headline',
		defaultMessage: 'Listen on-the-go with the mobile app',
		description: 'Headline for the Confluence mobile app promotion card in feed',
	},
	description: {
		id: 'feed.mobile-app-promo.description',
		defaultMessage:
			'Scan the QR code to listen to audio AI summaries with the Confluence mobile app. Perfect for your commute or when you need a screen break.',
		description: 'Description for the Confluence mobile app promotion card in feed',
	},
	proTip: {
		id: 'feed.mobile-app-promo.pro-tip',
		defaultMessage: 'PRO TIP',
		description: 'Short label badge on the mobile app promotion card in feed',
	},
	dismissLabel: {
		id: 'feed.mobile-app-promo.close',
		defaultMessage: 'Close',
		description: 'Tooltip label for the button dismissing the Confluence mobile app promotion card',
	},
	dismissAccessibleLabel: {
		id: 'feed.mobile-app-promo.dismiss.accessible-label',
		defaultMessage: 'Close mobile app promotion',
		description:
			'Accessible label for the button dismissing the Confluence mobile app promotion card',
	},
	qrAlt: {
		id: 'feed.mobile-app-promo.qr.alt',
		defaultMessage: 'QR code to download the Confluence mobile app',
		description: 'Alternative text for the Confluence mobile app QR code',
	},
	googlePlayAlt: {
		id: 'feed.mobile-app-promo.google-play.alt',
		defaultMessage: 'Get the Confluence mobile app on Google Play',
		description: 'Alternative text for the Google Play badge link',
	},
	appStoreAlt: {
		id: 'feed.mobile-app-promo.app-store.alt',
		defaultMessage: 'Download the Confluence mobile app on the App Store',
		description: 'Alternative text for the App Store badge link',
	},
});

const styles = cssMap({
	card: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: token('space.200'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.800'),
		backgroundColor: token('elevation.surface'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		boxSizing: 'border-box',
		marginBottom: token('space.200'),
	},
	leadBlock: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: token('space.200'),
		flex: '1 1 260px',
		minWidth: '0px',
	},
	logoTile: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.accent.blue.bolder'),
		flexShrink: 0,
	},
	eyebrow: {
		font: token('font.heading.xsmall'),
		color: token('color.text.brand'),
	},
	heading: {
		font: token('font.heading.small'),
	},
	message: {
		font: token('font.body'),
	},
	proTip: {
		alignSelf: 'start',
	},
	badgeLink: {
		display: 'inline-flex',
		alignItems: 'center',
		textDecoration: 'none',
	},
	badgeImage: {
		height: '30px',
		width: 'auto',
		display: 'block',
	},
	dismissButton: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 1,
	},
	qrCode: {
		width: '96px',
		height: '96px',
		flexShrink: 0,
		borderRadius: token('radius.small'),
		objectFit: 'contain',
		backgroundColor: token('elevation.surface.sunken'),
		display: 'block',
	},
	storeLinks: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		flexShrink: 0,
		marginLeft: 'auto',
		flexWrap: 'wrap',
		alignItems: 'flex-end',
	},
});

export const MobileAppPromo = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onAutoDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'autoDismissed',
				actionSubject: 'mobileAppPromo',
				source: 'homeScreen',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const { isMobileAppPromoDismissed, onMobileAppPromoDismiss, onMobileAppPromoView } =
		useMobileAppPromoDismiss({
			onAutoDismiss,
		});

	if (
		isMobileAppPromoDismissed ||
		!expVal('cc_mobile_app_promo_popular_feed', 'isEnabled', false)
	) {
		return null;
	}

	return <MobileAppPromoBanner onDismiss={onMobileAppPromoDismiss} onView={onMobileAppPromoView} />;
};

const MobileAppPromoBanner: FC<{
	onDismiss: () => void;
	onView?: () => void;
}> = ({ onDismiss, onView }) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		onView?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'mobileAppPromo',
				source: 'homeScreen',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'mobileAppPromoDismiss',
				source: 'homeScreen',
			},
		}).fire();
		onDismiss();
	}, [createAnalyticsEvent, onDismiss]);

	const handleBadgeClick = useCallback(
		(actionSubjectId: 'mobileAppPromoGooglePlayBadge' | 'mobileAppPromoAppStoreBadge') => () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId,
					source: 'homeScreen',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	return (
		<Box as="section" xcss={styles.card} testId="mobile-app-promo">
			<Box xcss={styles.dismissButton}>
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CrossIcon}
					label={intl.formatMessage(i18n.dismissAccessibleLabel)}
					tooltip={{ content: intl.formatMessage(i18n.dismissLabel) }}
					onClick={handleDismiss}
					isTooltipDisabled={false}
					testId="mobile-app-promo-dismiss"
				/>
			</Box>

			<Box xcss={styles.leadBlock}>
				<Box xcss={styles.logoTile} aria-hidden>
					<ConfluenceIcon appearance="brand" shouldUseNewLogoDesign />
				</Box>
				<Stack space="space.150">
					<Stack>
						<Box xcss={styles.eyebrow}>
							<FormattedMessage {...i18n.eyebrow} />
						</Box>
						<Box xcss={styles.heading}>
							<FormattedMessage {...i18n.headline} />
						</Box>
					</Stack>
					<Box xcss={styles.message}>
						<FormattedMessage {...i18n.description} />
					</Box>
					<Box xcss={styles.proTip} testId="mobile-app-promo-pro-tip">
						<Lozenge appearance="discovery">
							<FormattedMessage {...i18n.proTip} />
						</Lozenge>
					</Box>
				</Stack>
			</Box>

			<Box xcss={styles.qrCode}>
				<Image src={qrCode} alt={intl.formatMessage(i18n.qrAlt)} />
			</Box>

			<Box xcss={styles.storeLinks}>
				<Anchor
					xcss={styles.badgeLink}
					href={GOOGLE_PLAY_URL}
					target="_blank"
					newWindowLabel={intl.formatMessage(i18n.googlePlayAlt)}
					testId="mobile-app-promo-google-play"
					onClick={handleBadgeClick('mobileAppPromoGooglePlayBadge')}
				>
					<Box xcss={styles.badgeImage}>
						<Image src={googlePlayBadge} alt={intl.formatMessage(i18n.googlePlayAlt)} />
					</Box>
				</Anchor>
				<Anchor
					xcss={styles.badgeLink}
					href={APP_STORE_URL}
					target="_blank"
					newWindowLabel={intl.formatMessage(i18n.appStoreAlt)}
					testId="mobile-app-promo-apple-store"
					onClick={handleBadgeClick('mobileAppPromoAppStoreBadge')}
				>
					<Box xcss={styles.badgeImage}>
						<Image src={appStoreBadge} alt={intl.formatMessage(i18n.appStoreAlt)} />
					</Box>
				</Anchor>
			</Box>
		</Box>
	);
};
